// The app header. Inside the app the project menu is the sidebar (Sidebar.tsx, Marius's side
// navigation option B) and the header is the slim top bar beside it (AppTopBar): Menu (a phone),
// search · Ask · Feedback · Alerts · organisation · avatar.
// People and organisation (/org, switched off for now) keeps the design system's two-row Topbar
// (AppHeader, white, a hairline under it):
//   Tool row: project switcher · search · Ask · Feedback · Help · Alerts · organisation · avatar
//   Row 2: wordmark · Home · Activities · Experts · Stakeholders ·
//          Contract & finance · Results & reporting
// On People and organisation (/org...) row 2 shows that section's areas
// instead: Organisation · {the organisation's word} · Insight · My tasks, and
// a Settings gear at the end; the project chooser stays in row 1.
// Below 768px: a Search link replaces the box, the organisation menu moves
// into the avatar menu, and the nav collapses behind a "Menu" button.
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { PAGES, pageLink, type Me, type PageName } from "@convenor/shared";
import { api } from "../api/client";
import { useMeta } from "../api/meta";
import { lastProject, useDraftName, useProjectList, type ProjectListItem } from "../api/project";
import { useLogout, useSwitchOrg } from "../api/session";
import { initials } from "../ui/Avatar";
import { Menu } from "../ui/Menu";
import { AssistantPanel } from "../areas/assistant/AssistantPanel";
import { FeedbackButton } from "../areas/feedback-button";
import { orgNavItems, useOrgSection, type OrgNavKey } from "../areas/workforce/components/OrgLayout";
import { useOrgWords } from "../areas/workforce/queries";
import { hasOrgArea } from "./OrgAreaGate";
import { samePageIn } from "./same-page-in";
import { Wordmark } from "./Wordmark";

export type NavSection = "home" | "activities" | "experts" | "stakeholders" | "finance" | "reporting" | null;

type NavPage = Extract<PageName, "projectHome" | "activities" | "experts" | "stakeholders" | "adminOverview" | "reportingOverview">;

/** The main nav: each item is a project page; `path` is its address after /p/:projectId. */
export const NAV_ITEMS: { key: Exclude<NavSection, null>; label: string; page: NavPage; path: string }[] = (
  [
    { key: "home", label: "Home", page: "projectHome" },
    { key: "activities", label: "Activities", page: "activities" },
    { key: "experts", label: "Experts", page: "experts" },
    { key: "stakeholders", label: "Stakeholders", page: "stakeholders" },
    // Marius's split of Administration and reporting (4 Nov 2026). X-06 (audit round 4): the same
    // words as the breadcrumb and the page title (AdminFrame SECTION_TITLES).
    { key: "finance", label: "Contract & finance", page: "adminOverview" },
    { key: "reporting", label: "Results & reporting", page: "reportingOverview" },
  ] satisfies { key: Exclude<NavSection, null>; label: string; page: NavPage }[]
).map((item) => ({ ...item, path: PAGES[item.page].slice(PAGES.projectHome.length) }));

/** Which main-nav item is current for a path under /p/:projectId. */
export function navSection(pathname: string): NavSection {
  const m = /^\/p\/[^/]+(\/([^/]+))?(\/([^/]+))?/.exec(pathname);
  if (!m) return null;
  const first = m[2] ?? "";
  if (first === "") return "home";
  if (first === "activities") return "activities";
  if (first === "experts") return "experts";
  if (first === "stakeholders") return "stakeholders";
  // Marius's split of Administration and reporting (4 Nov 2026): Results & reporting is its
  // overview, Reach & network (the analytics) and the results and reports pages, which keep their
  // addresses under /admin. The rest of /admin is Contract & finance.
  if (first === "reporting" || first === "analytics") return "reporting";
  // The history of a result or indicator is a Results page; that of a budget line, a Budget page.
  if (first === "admin" && m[4] === "history") return /\/history\/budget_line(\/|$)/.test(pathname) ? "finance" : "reporting";
  if (first === "admin") return REPORTING_UNDER_ADMIN.has(m[4] ?? "") ? "reporting" : "finance";
  return null;
}

/** The pages under /p/:id/admin that belong to Results & reporting, by their first part after /admin. */
const REPORTING_UNDER_ADMIN = new Set(["logframe", "reports", "monthly", "financial-report"]);

/** The project the header shows: the URL's, else the last one opened. */
function useHeaderProject(projectId: string | null, projects: ProjectListItem[] | undefined) {
  const id = projectId ?? null;
  return projects?.find((p) => p.id === id) ?? null;
}

/** Which project the frame shows and leads into, for the sidebar and both headers. */
export function useShellProject(projectId: string | null) {
  const location = useLocation();
  const projects = useProjectList();
  const current = useHeaderProject(projectId, projects.data);
  // OB-F20 (audit round 4): a draft (the onboarding, /projects/new/:id/...) is no project to work in yet:
  // the menu never points at another project, and the switcher shows the draft's own name.
  const draftId = /^\/projects\/new\/([^/]+)/.exec(location.pathname)?.[1] ?? null;
  const draftName = useDraftName(draftId);
  const homeProject = draftId ? null : (projectId ?? lastProject() ?? projects.data?.[0]?.id ?? null);
  return { projects: projects.data ?? [], current, draftName: draftId ? (draftName ?? "New project") : null, homeProject };
}

function useUnreadCount(): number {
  const unread = useQuery({
    queryKey: ["notifications-count"],
    queryFn: () => api.get<{ unread: number }>("/api/notifications/count"),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
  return unread.data?.unread ?? 0;
}

/** The search box (a link on a phone). HM-F4 (audit round 4): outside a project the search covers all projects. */
function HeaderSearch({ projectId, homeProject }: { projectId: string | null; homeProject: string | null }) {
  const navigate = useNavigate();
  if (!homeProject) return null;
  // It never takes "the project" from another tab (lastProject is shared by every tab); the search
  // page only needs a project in its path to live under.
  const searchLabel = projectId ? "Search the project" : "Search all projects";
  const onSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get("q")?.toString().trim() ?? "";
    navigate(pageLink("search", { projectId: homeProject }, { query: `q=${encodeURIComponent(q)}${projectId ? "" : "&scope=all"}` }));
  };
  return (
    <>
      <form role="search" onSubmit={onSearch}>
        <label htmlFor="global-search" className="visually-hidden">
          {searchLabel}
        </label>
        <input id="global-search" name="q" type="search" placeholder={searchLabel} className="topbar__search" />
      </form>
      <Link className="btn btn--quiet topbar__search-link" to={pageLink("search", { projectId: homeProject }, projectId ? undefined : { query: { scope: "all" } })}>
        Search
      </Link>
    </>
  );
}

function AlertsLink() {
  const count = useUnreadCount();
  return (
    <Link className="btn btn--quiet" to={PAGES.notifications} aria-label={`Notifications, ${count} new`}>
      <span aria-hidden="true">Alerts</span>
      {count > 0 ? (
        <span className="topbar__count" aria-hidden="true">
          {count}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * The slim top bar beside the sidebar: Menu (opens the sidebar as a drawer on a phone), search,
 * Ask, Feedback, Alerts, the organisation and the avatar. Settings and Help are in the sidebar.
 */
export function AppTopBar({ me, projectId, homeProject, menuOpen, onMenu }: { me: Me; projectId: string | null; homeProject: string | null; menuOpen: boolean; onMenu: () => void }) {
  const meta = useMeta();
  /** The support assistant (docs/v3/ASSISTANT.md 1). */
  const [askOpen, setAskOpen] = useState(false);
  return (
    <header className="topbar">
      <span className="topbar__tools">
        <button type="button" className="btn btn--secondary sidebar-open" aria-expanded={menuOpen} aria-controls="sidebar" onClick={onMenu}>
          Menu
        </button>
        <HeaderSearch projectId={projectId} homeProject={homeProject} />
        {meta.demoMode ? null : (
          <button type="button" className="btn btn--quiet" aria-expanded={askOpen} onClick={() => setAskOpen((v) => !v)}>
            Ask
          </button>
        )}
        {meta.demoMode ? null : <FeedbackButton projectId={projectId} />}
        <AlertsLink />
        {/* The demo: the organisation's name only, no menu (nothing there works in a read-only demo). */}
        {meta.demoMode ? me.org ? <span className="topbar__org">{me.org.name}</span> : null : <OrgMenu me={me} projectId={homeProject} />}
        <AvatarMenu me={me} projectId={homeProject} demo={meta.demoMode} />
      </span>
      {askOpen && !meta.demoMode ? <AssistantPanel audience="team" onClose={() => setAskOpen(false)} /> : null}
    </header>
  );
}

export function AppHeader({ me, projectId }: { me: Me; projectId: string | null }) {
  const meta = useMeta();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  /** The support assistant (docs/v3/ASSISTANT.md 1). */
  const [askOpen, setAskOpen] = useState(false);
  const { projects, current, draftName, homeProject } = useShellProject(projectId);
  const draftId = draftName !== null;
  const section = navSection(location.pathname);
  const orgSectionKey = useOrgSection(location.pathname);
  // People and organisation only when the organisation has it switched on (OrgAreaGate).
  const org = hasOrgArea(me) ? orgSectionKey : null;

  useEffect(() => setMenuOpen(false), [location.pathname]);

  // X-37: one header on every page: the switcher shows the URL's project, or "Choose a project".
  const base = homeProject ? pageLink("projectHome", { projectId: homeProject }) : null;

  return (
    <header className={menuOpen ? "topbar topbar--two-row topbar--open" : "topbar topbar--two-row"}>
      <div className="topbar__toolrow">
        <ProjectSwitcher current={current} projects={projects} draftName={draftName} />
        <HeaderSearch projectId={projectId} homeProject={homeProject} />
        {meta.demoMode ? null : (
          <button type="button" className="btn btn--quiet" aria-expanded={askOpen} onClick={() => setAskOpen((v) => !v)}>
            Ask
          </button>
        )}
        {meta.demoMode ? null : <FeedbackButton projectId={projectId} />}
        <Link
          className={location.pathname === PAGES.help ? "btn btn--secondary" : "btn btn--quiet"}
          to={PAGES.help}
          state={{ from: `${location.pathname}${location.search}` }}
          aria-current={location.pathname === PAGES.help ? "page" : undefined}
        >
          Help
        </Link>
        <AlertsLink />
        {/* The demo: the organisation's name only, no menu (nothing there works in a read-only demo). */}
        {meta.demoMode ? me.org ? <span className="topbar__org">{me.org.name}</span> : null : <OrgMenu me={me} projectId={homeProject} />}
        <AvatarMenu me={me} projectId={homeProject} demo={meta.demoMode} />
      </div>
      <Wordmark to={base ?? PAGES.projects} />
      <button type="button" className="btn btn--secondary topbar__menu-btn" aria-expanded={menuOpen} aria-controls="main-nav" onClick={() => setMenuOpen((v) => !v)}>
        Menu
      </button>
      <nav id="main-nav" aria-label={org ? "People and organisation" : "Main"} className="topbar__nav">
        {org ? (
          <OrgNavList current={org} />
        ) : draftId ? null : (
          NAV_ITEMS.map((item) =>
            homeProject ? (
              <Link key={item.key} className="topbar__link" to={pageLink(item.page, { projectId: homeProject })} aria-current={section === item.key ? "page" : undefined}>
                {item.label}
              </Link>
            ) : (
              <Link key={item.key} className="topbar__link" to={PAGES.projects}>
                {item.label}
              </Link>
            ),
          )
        )}
      </nav>
      {askOpen && !meta.demoMode ? <AssistantPanel audience="team" onClose={() => setAskOpen(false)} /> : null}
    </header>
  );
}

/**
 * The People and organisation areas. The second item is the organisation's
 * own word (Samenwerkingsverbanden): until the settings (or this tab's last
 * known words) are there, it keeps its place but stays invisible, so the
 * header never flashes the default word.
 */
function OrgNavList({ current }: { current: OrgNavKey }) {
  const words = useOrgWords();
  const items = orgNavItems(words.collabLabelPlural);
  return (
    <>
      {items.map((item) =>
        item.key === "settings" ? (
          <Link key={item.key} className="topbar__link" to={item.to} aria-label={item.label} aria-current={current === item.key ? "page" : undefined}>
            <GearIcon />
            <span className="visually-hidden">{item.label}</span>
          </Link>
        ) : (
          <Link
            key={item.key}
            className="topbar__link"
            to={item.to}
            aria-current={current === item.key ? "page" : undefined}
            aria-busy={item.key === "collabs" && !words.ready ? true : undefined}
          >
            {item.key === "collabs" && !words.ready ? <span className="visually-hidden">{item.label}</span> : item.label}
          </Link>
        ),
      )}
    </>
  );
}

function GearIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M19.14 12.94a7.07 7.07 0 0 0 0-1.88l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.03 7.03 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 2.5h-3.84a.5.5 0 0 0-.49.42l-.36 2.54c-.59.24-1.14.55-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.67 8.98a.5.5 0 0 0 .12.64l2.03 1.58a7.07 7.07 0 0 0 0 1.88l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.39.3.6.22l2.39-.96c.49.39 1.04.7 1.63.94l.36 2.54c.04.24.25.42.49.42h3.84c.24 0 .45-.18.49-.42l.36-2.54c.59-.24 1.14-.55 1.63-.94l2.39.96c.22.08.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Z"
      />
    </svg>
  );
}

/** OB-F20: the name of one of my drafts (GET /api/projects lists them), once it has one. */

export function ProjectSwitcher({ current, projects, draftName = null }: { current: ProjectListItem | null; projects: ProjectListItem[]; draftName?: string | null }) {
  const others = projects.filter((p) => p.id !== current?.id && p.status !== "closed");
  // R2-007 (D9): another project opens on the page you are on (a record page: its section's list).
  const { pathname } = useLocation();
  return (
    <Menu
      align="left"
      className="project-switcher"
      label={
        <>
          <span className="project-switcher__label">Project</span>{" "}
          {/* HM-F3: the name only (the reference is in the list and on hover), so the tool row stays on one line. */}
          <b className="project-switcher__name" title={current ? `${current.name} · ${current.reference}` : undefined}>
            {draftName !== null ? `Draft: ${draftName}` : current ? current.name : "Choose a project"}
          </b>
        </>
      }
    >
      {current ? (
        <li>
          <Link to={pageLink("projectHome", { projectId: current.id })} aria-current="page">
            {current.name} · {current.reference}
          </Link>
        </li>
      ) : null}
      {others.slice(0, 8).map((p) => (
        <li key={p.id}>
          <Link to={samePageIn(pathname, p.id)}>
            {p.name} · {p.reference}
          </Link>
        </li>
      ))}
      <li className="menu__separator" role="separator" />
      <li>
        <Link to={PAGES.projects}>All projects</Link>
      </li>
    </Menu>
  );
}

function OrgMenu({ me, projectId, inline = false }: { me: Me; projectId: string | null; inline?: boolean }) {
  const switchOrg = useSwitchOrg();
  const navigate = useNavigate();
  if (!me.org) return null;
  const others = me.orgs.filter((o) => o.id !== me.org?.id);
  const items = (
    <>
      <li className="menu__heading">{me.org.name}</li>
      <li>
        <Link to={PAGES.settingsTeam}>Organisation settings</Link>
      </li>
      {hasOrgArea(me) ? (
        <li>
          <Link to="/org">People and organisation</Link>
        </li>
      ) : null}
      {projectId ? (
        <li>
          <Link to={pageLink("settingsPartners", { projectId })}>Project partners</Link>
        </li>
      ) : null}
      {others.map((o) => (
        <li key={o.id}>
          <button type="button" onClick={() => switchOrg.mutate({ orgId: o.id }, { onSuccess: () => navigate(PAGES.projects) })}>
            Switch to {o.name}
          </button>
        </li>
      ))}
      <li>
        <Link to={PAGES.welcomeOrganisation}>Create an organisation</Link>
      </li>
    </>
  );
  if (inline) return items;
  return (
    <Menu
      className="org-menu"
      label={
        <>
          <span className="visually-hidden">Organisation: </span>
          <span className="org-menu__name">{me.org.name}</span>
        </>
      }
    >
      {items}
    </Menu>
  );
}

function AvatarMenu({ me, projectId, demo }: { me: Me; projectId: string | null; demo: boolean }) {
  const logout = useLogout();
  const navigate = useNavigate();
  return (
    <Menu
      className="avatar-menu"
      label={
        <>
          <span className="avatar avatar--s avatar--me" aria-hidden="true">
            {initials(me.user.name) || "?"}
          </span>
          <span className="visually-hidden">My account</span>
        </>
      }
    >
      <li className="menu__heading">{me.user.name}</li>
      <li>
        <Link to={PAGES.settingsAccount}>My account</Link>
      </li>
      {/* GET /api/me is the v3 Me; the v1 type does not know the staff flag yet. */}
      {(me.user as { platformAdmin?: boolean }).platformAdmin ? (
        <>
          <li>
            <Link to={PAGES.staffEmails}>Email log</Link>
          </li>
          <li>
            <Link to={PAGES.staffTrials}>Trials</Link>
          </li>
        </>
      ) : null}
      {demo ? null : (
        <li className="avatar-menu__org">
          <ul className="avatar-menu__org-list">
            <OrgMenu me={me} projectId={projectId} inline />
          </ul>
        </li>
      )}
      <li>
        <button type="button" onClick={() => logout.mutate(undefined, { onSettled: () => navigate(PAGES.signIn) })}>
          Sign out
        </button>
      </li>
    </Menu>
  );
}
